<template>
    <div class="ppv2-max-w-4xl ppv2-mx-auto ppv2-p-6">
        <!-- Error Messages -->
        <InfoMessageBox
            v-if="isDirty && Object.keys(errors).length > 0"
            :message="Object.values(errors).join(' ')"
            color="danger"
            class="ppv2-mb-4"
        />

        <!-- Week Range Selection -->
        <div class="ppv2-rounded-lg ppv2-shadow-sm ppv2-p-6 ppv2-mb-6">
            <h2 class="ppv2-text-lg poppinsSemiBold ppv2-text-gray-900 ppv2-mb-4">
                {{ $t('resource_planning.weekRangeScheduling.title') }}
            </h2>

            <!-- Label Input -->
            <div class="ppv2-mb-6">
                <BaseInput
                    v-model="label"
                    :label="$t('resource_planning.weekRangeScheduling.period_label')"
                    :placeholder="$t('resource_planning.weekRangeScheduling.period_placeholder')"
                    class="ppv2-mt-1"
                    @change="updateLabel"
                    :required="true"
                />
            </div>

            <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-gap-6 ppv2-mb-6">
                <!-- Start Week -->
                <div>
                    <DateInput
                        v-model="startDate"
                        :disabled="mode === 'edit'"
                        :min-date="minDate"
                        :max-date="endDate"
                        :placeholder="$t('resource_planning.weekRangeScheduling.period_date_placeholder')"
                        class="ppv2-mt-1"
                        @input="updateWeekRange"
                        :required="true"
                        :label="$t('resource_planning.weekRangeScheduling.date_begin_label')"
                    />
                </div>

                <!-- End Week -->
                <div>
                    <DateInput
                        v-model="endDate"
                        :disabled="!startDate || mode === 'edit'"
                        :min-date="startDate || minDate"
                        :placeholder="$t('resource_planning.weekRangeScheduling.period_date_placeholder')"
                        class="ppv2-mt-1"
                        @input="updateWeekRange"
                        :required="true"
                        :label="$t('resource_planning.weekRangeScheduling.date_end_label')"
                    />
                </div>
            </div>

            <!-- Day Planning for Selected Week -->
            <div class="ppv2-mt-8">
                <h3 v-if="startDate && endDate" class="ppv2-text-lg poppinsMedium ppv2-mb-4">
                    Planification du {{ DateFrString(startDate) }} au {{ DateFrString(endDate) }}
                </h3>
                <DayPlanning
                    :disabled="!startDate || !endDate"
                    :days="weekDays"
                    :showAll="mode != 'edit'"
                    @update:days="updateWeekDays"
                    input-type="switch"
                />
            </div>
        </div>
    </div>
</template>

<script>
import ApiEstablishmentManager from '../../../helpers/ApiEstablishmentManager'
import DayPlanning from './DayPlanning.vue'
import DateInput from '../../Shared/DateInput.vue'
import BaseInput from '../../Shared/BaseInput.vue'
import InfoMessageBox from '../../Shared/InfoMessageBox.vue'

export default {
    name: 'WeekRangePlanning',
    components: {
        DayPlanning,
        DateInput,
        InfoMessageBox,
        BaseInput,
    },
    props: {
        mode: {
            type: String,
            default: 'add',
        },
        selectedSchedule: {
            type: Object,
            default: () => null,
        },
    },
    data() {
        const today = new Date()

        return {
            startDate: null,
            endDate: null,
            weekDays: [],
            minDate: today,
            label: '',
            errors: {},
            isDirty: false,
            isInitialized: false,
        }
    },
    methods: {
        /**
         * Add or update an error message for a specific key
         * @param {string} key - The error key
         * @param {string} message - The error message (empty string to remove)
         * @returns {Object} The updated errors object
         */
        putError(key, message) {
            if (message) {
                // Add or update the error
                this.$set(this.errors, key, message)
            } else {
                // If message is falsy, remove the error
                this.removeError(key)
            }
            return this.errors
        },

        /**
         * Remove an error by its key
         * @param {string} key - The error key to remove
         * @returns {Object} The updated errors object
         */
        removeError(key) {
            if (this.errors.hasOwnProperty(key)) {
                const newErrors = { ...this.errors }
                delete newErrors[key]
                this.errors = newErrors
            }
            return this.errors
        },

        /**
         * Get an error message by key
         * @param {string} key - The error key to retrieve
         * @returns {string} The error message or empty string if not found
         */
        getError(key) {
            return this.errors[key] || ''
        },

        /**
         * Check if there are any errors
         * @returns {boolean} True if there are no errors
         */
        isValid() {
            return Object.keys(this.errors).length === 0
        },
        DateFrString(dateString) {
            const options = {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
            }

            const formatter = new Intl.DateTimeFormat('fr-FR', options)

            const formatted = formatter.format(new Date(dateString))

            return formatted
        },
        updateWeekRange() {
            if (this.isInitialized) {
                this.isDirty = true
            }
            if (this.startDate && this.endDate) {
                this.initializeWeekDays()
            }
        },
        init() {
            if (this.mode === 'edit') {
                const firstDateStr = this.selectedSchedule?.days?.find((d) => !d.isDefault && d.rawData?.date)?.rawData.date
                const lastDateStr = this.selectedSchedule?.days?.find(
                    (d) => d.isDefault && d.rawData?.date && d.rawData.date > firstDateStr
                )?.rawData.date

                this.label = this.selectedSchedule.title

                if (firstDateStr) {
                    const startDate = new Date(firstDateStr)
                    // Get day index (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
                    const day = startDate.getDay()
                    // ISO: Adjust so Monday is the first day of the week
                    const diffToMonday = (day === 0 ? -6 : 1) - day // if Sunday (0), go back 6 days
                    const monday = new Date(startDate)
                    monday.setDate(startDate.getDate() + diffToMonday)
                    const sunday = new Date(monday)
                    sunday.setDate(monday.getDate() + 6)
                    const formatDate = (d) => d.toISOString().split('T')[0]
                    this.startDate = formatDate(startDate)
                    if (lastDateStr) {
                        const endDate = new Date(lastDateStr)
                        endDate.setDate(endDate.getDate() - 1)
                        this.endDate = formatDate(endDate)
                    } else {
                        this.endDate = formatDate(sunday)
                    }
                    this.updateWeekRange()
                }
            }
        },
        /**
         * Get the Monday date of an ISO week string like "2025-W36"
         */
        getISOMondayFromDate(dateStr) {
            const date = new Date(dateStr)
            const day = date.getDay() // 0 (Sun) to 6 (Sat)

            // Calculate difference to Monday
            const diffToMonday = (day === 0 ? -6 : 1) - day // Sunday (0) becomes -6, Monday (1) becomes 0, etc.

            const monday = new Date(date)
            monday.setDate(date.getDate() + diffToMonday)
            monday.setHours(0, 0, 0, 0)

            return monday
        },
        initializeWeekDays() {
            if (!this.startDate || !this.endDate) return
            // Get the  ISO Monday
            const monday = this.getISOMondayFromDate(this.startDate)
            const dayLabels = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']
            const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
            const dayAbbr = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
            const dayKeys = [1, 2, 3, 4, 5, 6, 0]

            const weekDays = []

            for (let i = 0; i < 7; i++) {
                const mondayUTC = new Date(Date.UTC(monday.getFullYear(), monday.getMonth(), monday.getDate()))
                const currentDate = new Date(mondayUTC)
                currentDate.setUTCDate(mondayUTC.getUTCDate() + i)
                const isoDate = currentDate.toISOString().split('T')[0]
                const abbr = dayAbbr[i]
                const matched = this.selectedSchedule?.days?.find((d) => d.name === abbr)

                weekDays.push({
                    name: dayNames[i],
                    label: dayLabels[i],
                    date: isoDate,
                    day: dayKeys[i],
                    enabled: this.mode === 'edit' ? matched?.isClosed === false : true,
                    periods: matched?.rawData?.periods || [],
                    lunches: matched?.rawData?.lunches || [],
                    title: this.mode === 'edit' ? matched?.title : '',
                })
            }
            this.weekDays = weekDays
        },
        updateLabel() {
            if (this.isInitialized) {
                this.isDirty = true
            }
            this.sendDate()
        },
        sendDate() {
            this.checkInputs()
            const formattedData = ApiEstablishmentManager.formatDaysForApi(this.weekDays, this.label)
            const dataToSend = {
                scheduling: formattedData,
                start: this.startDate,
                end: this.endDate,
            }
            // Format data for API

            // Emit save event with data and validation status
            this.$emit('save', dataToSend, this.isValid())

            return
        },

        checkInputs() {
            this.removeError('validation')
            // Basic validation
            if (!this.startDate || !this.endDate) {
                this.putError('validation', this.$t('validation.date_range_required'))
            }

            if (!this.weekDays || !this.weekDays.length) {
                this.putError('validation', this.$t('validation.schedule_data_required'))
            }
            if (!this.label || this.label.trim().length === 0) {
                this.putError('validation', this.$t('validation.label_required'))
            }
        },

        updateWeekDays(updatedDays, isValidData) {
            if (this.isInitialized) {
                this.isDirty = true
            }
            // Clear previous validation errors
            this.removeError('planning')
            // Basic validation
            if (!isValidData) {
                this.putError('planning', this.$t('validation.check_planning'))
            }

            // Update week days
            this.weekDays = [...updatedDays]
            this.sendDate()
        },
    },
    mounted() {
        this.init()
        this.$nextTick(() => {
            this.isInitialized = true
        })
    },
}
</script>
